import { getSupplierStatusLabel } from '@/shared/utils/labelConverterHelpers';
import { formatDateToDisplay, formatBirthDate, formatPhoneNumber, formatBusinessNumber } from '@/shared/utils/formatter';
import { SupplierDto, SupplierDetailDto, SupplierListResponseDto, SupplierDetailListResponseDto, SupplierInfoDto } from '../types/supplier.api.types';
import { Supplier, SupplierDetail, SupplierList, SupplierDetailList } from '../types/supplier.types';
import { SupplierInfo } from '../types/supplierInfo.types';

const extractStandardArea = (deliveryArea: string | null | undefined): string => {
    if (!deliveryArea) return '-';
    const match = deliveryArea.match(/^([^\s(]+)/);
    return match ? match[1] : '-';
};

const getTaxTypeLabel = (taxType: string | null | undefined): string => {
    if (!taxType) return '-';
    const taxTypeMap: Record<string, string> = {
        GENERAL: '일반과세자',
        SIMPLIFIED: '간이과세자',
        EXEMPT: '면세사업자',
    };
    return taxTypeMap[taxType] || taxType;
};

export const getBankName = (bankCode: string | null): string => {
    if (!bankCode) return '';
    const bankMap: Record<string, string> = {
        '011': '농협은행',
        '004': '국민은행',
        '088': '신한은행',
        '020': '우리은행',
        '081': '하나은행',
        '003': '기업은행',
        '023': 'SC제일은행',
        '027': '한국씨티은행',
        '신한은행': '신한은행',
    };
    return bankMap[bankCode] || bankCode;
};

export const supplierFromDto = (dto: SupplierDto): Supplier => ({
    id: dto.suppId,
    name: dto.suppName,
});

export const supplierDetailFromDto = (dto: SupplierDetailDto): SupplierDetail => ({
    id: dto.suppId,
    name: dto.suppName,
    phoneNumber: dto.mngPhoneNum || '-',
    operationStatus: getSupplierStatusLabel(dto.statusCode),
    suppType: dto.suppType === 'F' ? '가맹' : dto.suppType === 'D' ? '직영' : (dto.suppType || '-'),
    grade: dto.gradeName || dto.suppGrade || '-',
    registrationDate: formatDateToDisplay(dto.insDate) || '-',
    endDate: formatDateToDisplay(dto.endMngDay || dto.endDate) || '-',
    ownerName: dto.compCeoName || '-',
    companyName: dto.suppCompName || '-',
    businessNumber: dto.suppNum || '-',
    accountHolder: dto.acctName || '-',
    bankName: dto.bankName || '-',
    accountNumber: dto.accountNum || '-',
    email: dto.email || '-',
    address: dto.addrInfo || '-',
    bizType: dto.bizType || '-',
    bizItem: dto.bizItem || '-',
    taxType: getTaxTypeLabel(dto.taxType),
    ratePlan: '-',
    standardArea: extractStandardArea(dto.deliveryArea),
    slotSetCnt: dto.slotSetCnt != null ? String(dto.slotSetCnt) : '-',
});

export const supplierListFromDto = (dto: SupplierListResponseDto): SupplierList => ({
    totalCount: dto.totalCount,
    suppliers: dto.suppliers.map(supplierFromDto),
});

export const supplierDetailListFromDto = (dto: SupplierDetailListResponseDto): SupplierDetailList => ({
    totalCount: dto.totalCount,
    suppliers: dto.suppliers.map(supplierDetailFromDto),
});

export const supplierInfoFromDto = (dto: SupplierInfoDto): SupplierInfo => ({
    supplierId: dto.suppId,
    supplierName: dto.suppName,
    supplierGrade: dto.gradeName || '-',
    suppLvl: dto.suppLvl || '',
    slotSetCnt: dto.slotSetCnt != null ? String(dto.slotSetCnt) : '',
    mobilePhone: formatPhoneNumber(dto.mngPhoneNum),
    status: getSupplierStatusLabel(dto.statusCode),
    registrationDate: dto.insDate ? formatBirthDate(dto.insDate) : '-',
    operationEndDate: dto.endMngDay ? formatBirthDate(dto.endMngDay) : '-',
    phone: '',
    address: dto.addrInfo || '',
    deliveryArea: dto.deliveryArea || '',
    standardArea: extractStandardArea(dto.deliveryArea),
    suppType: dto.suppType === 'F' ? '가맹' : dto.suppType === 'D' ? '직영' : (dto.suppType || '-'),
    bankCode: dto.bankCode || '',
    bankName: dto.bankName || getBankName(dto.bankCode),
    accountNumber: dto.accountNum || '',
    accountHolder: dto.acctName || '',
    companyName: dto.suppCompName,
    businessNumber: formatBusinessNumber(dto.suppNum),
    representative: dto.compCeoName,
    bizType: dto.bizType || '',
    bizItem: dto.bizItem || '',
    bizClass: dto.bizClass || '',
    taxType: dto.taxType || '',
    email: dto.email || '',
    dpBankCode: dto.dpBankCd || '',
    dpBankName: dto.dpBankCd ? getBankName(dto.dpBankCd) : '',
    dpAccountNumber: dto.dpAcctNo || '',
    dpAccountName: dto.dpAccName || '',
});
